import { useMemo } from 'react'
import { Stack, Alert } from '@mui/material'
import { useWalkTimeline } from '~/api/hooks/useCharts'
import { DimNotApplicableError } from '~/api/types'
import { useStore } from '~/state/useStore'
import { useValueTypeFilterState } from '~/state/useValueTypeFilterState'
import { pivot, seriesKeysWindowFirst } from '~/components/Charts/TimelineChart'
import {
  assignGroups,
  summariseByUnit,
  fillGaps,
  partitionWithRemainder,
  computeSeriesSums,
  splitBaseline,
  baselineBoundaryIso,
  type SeriesPoint,
} from '~/components/Charts/dataPipeline'
import {
  formatPeriodRangeLabel,
  formatFyPeriodRangeLabel,
} from '~/components/Charts/SummaryBarStrip'
import { buildFyTickFormatter } from '~/components/Charts/fyTickFormatter'
import { buildSummaryMatrix, SummarySeriesMatrix } from '~/components/Charts/SummarySeriesMatrix'
import { HiddenRemainderNote } from '~/components/Charts/HiddenRemainderNote'
import { PinButton } from '~/components/CollectionPin/PinButton'
import { ChartCardHeader } from '~/components/Charts/chrome/ChartCardHeader'
import { ChartContextCaption } from '~/components/Charts/chrome/ChartContextCaption'
import { DimNotApplicableNote } from './DimNotApplicableNote'
import { FilterApplicabilityNote } from './FilterApplicabilityNote'
import { ChartLoadingState } from './ChartLoadingState'
import { PendingOverlay } from './PendingOverlay'
import {
  buildChartTitle,
  buildContextCaption,
  timeframePhrase,
  KICKER_BY_CHART_TYPE,
  MESSAGES,
} from '~/components/Charts/chrome/chartChrome'
import { buildPinV1 } from '~/pin/buildPinV1'
import { useValueTypeLabels } from '~/api/hooks/useValueTypeLabels'
import { useSeriesBaseLabels } from './useSeriesBaseLabels'
import { useTimeframeLabel } from './useTimeframeLabel'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import { useDimValueColorForBase } from '~/components/Charts/useDimValueColorForBase'
import { distinctSortedValueTypes } from './distinctSortedValueTypes'
import { resolveEffectiveVts } from './resolveEffectiveVts'
import { composeChartTitle } from './composeChartTitle'
import { useCampaignHasNoRows } from './useCampaignHasNoRows'
import type { ReviewPageState } from './useReviewPageState'

interface Props {
  state: ReviewPageState
  /** Rider 3: when compare mode is active, the compare overlay is the authoritative
   *  cross-period view; a URL-carried panel_base extending the strip walk would add a
   *  previous-period column that conflicts with the main tile's compare overlay. Suppress it. */
  suppressBaseline?: boolean
}

/** Per-period per-series overview matrix. Panels aligned to the page's granularity dots (same
 *  partitioning as the main TimelineTile). By D1 the summary slot is always walk_timeline, so
 *  this fires the same walk as the main TimelineTile (react-query dedupes → no extra fetch).
 *  Pinnable: builds a walk_timeline/summary_bar_strip pin from page state.
 *  Value-type filtering: mirrors ChartTile's useValueTypeFilterState + resolveEffectiveVts trio
 *  so series-key namespaces stay in lockstep with the main tile.
 *  Coloring: value-keyed via the SAME useDimValueColorForBase catalog resolver as ChartTile
 *  (spec D3) — a dim value renders the identical color in the strip and in the chart beside it,
 *  and never repaints on filtering (positional/index-based coloring would do both wrong). */
export function SummaryStripTile({ state, suppressBaseline = false }: Props) {
  // Row 15-light: same from-extension as TimelineTile — the params object must be
  // structurally IDENTICAL to the main tile's so react-query dedupes into ONE walk fetch.
  // state.baseParams itself stays the UNEXTENDED window (window integrity).
  // Rider 3: treat panelBaseline as null when compare is active (suppress the previous-period column).
  const baselineFromIso = suppressBaseline ? null : (state.panelBaseline?.baselineFromIso ?? null)
  const walkParams = baselineFromIso
    ? { ...state.baseParams, from: baselineFromIso }
    : state.baseParams
  const walk = useWalkTimeline(walkParams)

  // In-window rows (boundary rule shared with ChartTile/splitBaseline) — vt options and
  // the matrix's in-window columns must be identical with the flag on or off.
  const boundary = baselineFromIso
    ? baselineBoundaryIso(state.fromIso, state.governance.effectiveUnit)
    : null
  const inWindowData = useMemo(
    () =>
      boundary == null
        ? (walk.data ?? [])
        : (walk.data ?? []).filter((r) => String(r.time) >= boundary),
    [walk.data, boundary],
  )

  // Mirror ChartTile's value-type filtering exactly so the strip pivots the SAME filtered rows.
  // Same URL-persisted state (useValueTypeFilterState), same resolver (resolveEffectiveVts)
  // → series keys stay in lockstep (colors are value-keyed via colorForBase below, not positional).
  const [selectedVts] = useValueTypeFilterState()
  const allVts = useMemo(() => distinctSortedValueTypes(inWindowData), [inWindowData])
  const effectiveVts = resolveEffectiveVts(selectedVts, allVts)
  const filteredRows = useMemo(
    () =>
      (walk.data ?? []).filter((r) => r.value_type == null || effectiveVts.includes(r.value_type)),
    [walk.data, effectiveVts],
  )

  // Label derivation — mirrored from ChartTile (TimelineTile) so series labels match main legend.
  const vtLabels = useValueTypeLabels(state.factTable)
  const seriesBaseLabelFor = useSeriesBaseLabels(state.factTable, state.groupBy)
  const palette = useStore((s) => s.personal.chipPalette)
  // Chrome inputs (rows 4/5/25) — hooks stay ABOVE the loading/error early returns.
  const mainTimeframeLabel = useTimeframeLabel(state)
  const valueLabelFor = useDimValueLabelFor(state.factTable)
  // Color catalog (spec D3) — UNCONDITIONAL, same wiring as ChartTile/AbsoluteTile so the
  // strip's series colors match the main chart tile beside it (same catalog, same slot).
  const colorForBase = useDimValueColorForBase(state.groupBy, state.factTable, state.kpi)
  // D-FEW-5: sharper honest-empty — UNCONDITIONAL, before the loading/error early returns below.
  const campaignHasNoRows = useCampaignHasNoRows(state.campaign, state.factTable, state.kpi)

  const pin = buildPinV1({
    walk_function: 'walk_timeline',
    fact_table: state.factTable,
    kpi: state.kpi,
    // FY v2: freeze the RAW display unit (not governance.effectiveUnit) — replay must
    // reproduce what THIS tile folds at (ChartCanvas reads the raw unit), spec §3.2.
    render_unit: state.granularity.unit,
    params: {
      from: state.fromIso,
      to: state.toIso,
      // T4b: freeze exactly the walk's fired filter_dims (baseParams — T2 campaign-merged),
      // not the pre-merge state.filters. walkParams never overrides filter_dims (only `from`
      // under a baseline panel), so this is always what the walk above actually fired with.
      filter_dims: state.baseParams.filter_dims,
      group_by: state.groupBy,
      bucket_size: state.baseParams.bucket_size,
      value_types: effectiveVts,
    },
    chart_type: 'summary_bar_strip',
  })

  if (walk.isLoading) {
    return <ChartLoadingState />
  }
  if (walk.error) {
    return walk.error instanceof DimNotApplicableError ? (
      <DimNotApplicableNote
        dims={walk.error.dims}
        metricLabel={composeChartTitle(state.kpiCascade)}
      />
    ) : (
      <Alert severity="warning">{MESSAGES.overviewLoadFailed}</Alert>
    )
  }

  // Build panels aligned to the page's gran dots (mirrors buildTimelinePoints' volume path).
  // Uses filteredRows (post value-type filter, EXTENDED when a baseline is active) so series
  // keys mirror the main tile exactly — incl. the window-first key ordering under a baseline.
  const piv = pivot(filteredRows)
  const seriesKeys =
    boundary == null ? piv.seriesKeys : seriesKeysWindowFirst(filteredRows, boundary)
  const groups = assignGroups(seriesKeys, palette, vtLabels, seriesBaseLabelFor, colorForBase)
  // T6 audit (raw vs governed): the GOVERNED unit is correct here — the strip's column labels are
  // built from the bucket TIMES (formatPeriodRangeLabel), so folding must land on the walk's true
  // bucket starts. Under FY this is not even a choice: FY is off the coarsening ladder, so
  // requested 'fy' ⇔ effective 'fy' and this read equals ChartCanvas's raw one.
  const coarsened = summariseByUnit(
    fillGaps(piv.points, groups),
    state.governance.effectiveUnit,
    groups,
  )
  // Row 15-light: split off the pre-window points; partitionWithRemainder (and the strip's
  // remainder note) runs on IN-WINDOW points only — the column layout cannot shift.
  let baselinePoints: SeriesPoint[] = []
  let inWindowPoints = coarsened
  if (baselineFromIso) {
    const split = splitBaseline(coarsened, state.fromIso, state.governance.effectiveUnit)
    baselinePoints = split.baseline
    inWindowPoints = split.inWindow
  }
  const { panels, hidden } = partitionWithRemainder(inWindowPoints, state.granularity.dots)

  // FY v2 (spec §5): FY column labels. Window = the bounds the walk ACTUALLY fetched
  // (baselineFromIso when the previous-period column is on) — same I1 rule as ChartCanvas.
  const fyLabelOf =
    state.granularity.unit === 'fy'
      ? buildFyTickFormatter(baselineFromIso ?? state.fromIso, state.toIso)
      : null

  const perPanel = [
    // Baseline column first (isBaseline: grayed, no own Δ; the first in-window column
    // computes its Δ against it). Empty pre-window → empty sums map ('Σ –', not 0).
    ...(baselineFromIso
      ? [
          {
            label: MESSAGES.previousPeriod,
            isBaseline: true,
            sums:
              baselinePoints.length === 0
                ? new Map<string, number | null>()
                : computeSeriesSums(baselinePoints, groups),
          },
        ]
      : []),
    ...panels.map((panel) => ({
      label: fyLabelOf ? formatFyPeriodRangeLabel(panel, fyLabelOf) : formatPeriodRangeLabel(panel),
      sums: computeSeriesSums(panel, groups),
    })),
  ]

  const { columns: matrixColumns, zeroAxisFraction } = buildSummaryMatrix(perPanel, groups)

  // Chrome (rows 4/5/25): the strip mirrors the main tile's title — same single-vt unit rule.
  const singleVtLabel = effectiveVts.length === 1 ? (vtLabels[effectiveVts[0]!] ?? null) : null
  const tfPhrase = timeframePhrase([mainTimeframeLabel])
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    valueTypeLabel: singleVtLabel,
    timeframePhrase: tfPhrase,
  })
  // T4b: caption mirrors the pin — the walk's actual filter_dims, not the pre-merge state.filters.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  return (
    <PendingOverlay active={walk.isPlaceholderData}>
      <Stack spacing={0.5}>
        <ChartCardHeader
          kicker={KICKER_BY_CHART_TYPE['summary_bar_strip']!}
          title={cardTitle}
          actions={<PinButton pin={pin} />}
        />
        <SummarySeriesMatrix
          columns={matrixColumns}
          groups={groups}
          pinId={`${state.pageId}:summary_matrix`}
          campaignLabel={state.campaign?.label ?? null}
          campaignHasNoRows={campaignHasNoRows}
          zeroAxisFraction={zeroAxisFraction}
        />
        <HiddenRemainderNote count={hidden.length} />
        <FilterApplicabilityNote state={state} />
        <ChartContextCaption lines={captionLines} />
      </Stack>
    </PendingOverlay>
  )
}
